import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Boxes,
  MapPin,
  ArrowRight,
  Tag,
  Layers,
  Sparkles,
  Clock,
  Leaf,
  Recycle,
  RotateCcw,
  Smartphone,
  Camera,
  Cable,
  Monitor,
  Tv,
  ShieldHalf,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à Mareil-sur-Mauldre ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Service de livraison de vos cartons {city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que courir les zones commerciales de la région
              pour charger des piles de cartons encombrants dans
              votre véhicule est une perte de temps précieuse, notre
              service de proximité gère tout depuis notre plateforme.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Une dépose directe chez vous",
                body: "Nous acheminons l'intégralité de votre commande à plat sur le pas de votre porte à Mareil-sur-Mauldre, mais également dans les communes voisines comme Maule, Beynes, Montainville, Herbeville et Crespières.",
                Icon: MapPin,
              },
              {
                title: "La garantie de la ponctualité",
                body: "Nous fixons ensemble un créneau horaire précis et respecté pour vous éviter de bloquer votre journée à attendre le livreur.",
                Icon: Clock,
              },
              {
                title: "Des fournitures impeccables",
                body: "Tous vos cartons et protections vous sont remis au sec, parfaitement propres et prêts à l'usage pour attaquer vos pièces sans attendre.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — L'après-déménagement & seconde vie (éco-zéro déchet) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="overline inline-flex items-center gap-2" style={{ color: "#237cb5" }}>
              <Leaf className="h-3.5 w-3.5" strokeWidth={2.8} />
              Démarche zéro déchet
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b] leading-tight">
              L&apos;après-déménagement : valorisation et seconde vie
              de vos cartons
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Une fois installé dans votre nouveau logement
              {" "}{city.aCity}, la gestion des dizaines de cartons
              vides peut vite devenir encombrante. Nous avons pensé
              à tout pour vous simplifier la vie.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/cartons-reconditionnes"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
                data-testid="city-mareil-sur-mauldre-cta-reconditionnes"
              >
                <Recycle className="h-4 w-4" strokeWidth={2.6} />
                Voir notre filière reconditionnée
                <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="relative bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 overflow-hidden">
              <span className="absolute top-0 left-0 h-1.5 w-full bg-[#237cb5]" />
              <span className="flex items-center justify-center h-11 w-11 rounded-xl bg-[#237cb5] text-white">
                <Leaf className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="mt-4 font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                100 % recyclable
              </h3>
              <p className="mt-2 text-[13px] text-[#3a4856] leading-relaxed">
                Fibres de cellulose biodégradables, conformes aux
                circuits de tri français.
              </p>
            </div>
            <div className="relative bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 overflow-hidden">
              <span className="absolute top-0 left-0 h-1.5 w-full bg-[#237cb5]" />
              <span className="flex items-center justify-center h-11 w-11 rounded-xl bg-[#237cb5] text-white">
                <Layers className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="mt-4 font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                Repliage à plat
              </h3>
              <p className="mt-2 text-[13px] text-[#3a4856] leading-relaxed">
                Après usage, repliez en quelques secondes pour
                stocker dans un coin de garage ou prêter à vos
                proches.
              </p>
            </div>
            <div className="relative bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 overflow-hidden">
              <span className="absolute top-0 left-0 h-1.5 w-full bg-[#FF5722]" />
              <span className="flex items-center justify-center h-11 w-11 rounded-xl bg-[#FF5722] text-white">
                <RotateCcw className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="mt-4 font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                Reconditionnement local
              </h3>
              <p className="mt-2 text-[13px] text-[#3a4856] leading-relaxed">
                Sollicitez-nous pour réintégrer vos cartons dans
                notre circuit de seconde vie à −40 %.
              </p>
            </div>

            {/* Bandeau full width écoresponsable */}
            <div className="sm:col-span-3 relative bg-[#0f1d2b] text-white rounded-2xl p-5 md:p-6 overflow-hidden">
              <div className="flex items-start gap-4">
                <span className="flex items-center justify-center shrink-0 h-12 w-12 rounded-xl bg-[#FF5722] text-white">
                  <Recycle className="h-6 w-6" strokeWidth={2.4} />
                </span>
                <div>
                  <div className="text-[10.5px] tracking-[0.18em] uppercase font-extrabold text-[#FF5722]">
                    Engagement durable
                  </div>
                  <h3 className="mt-1 font-display font-extrabold text-[17px] text-white leading-tight">
                    Un projet respectueux de l&apos;environnement, du
                    début à la fin
                  </h3>
                  <p className="mt-2 text-[13.5px] text-white/80 leading-relaxed">
                    Du premier carton plié au dernier replié, vous
                    pilotez un déménagement bas-carbone sans
                    compromis sur la protection de vos biens.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Télétravail & multimédia (UNIQUE) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline inline-flex items-center gap-2" style={{ color: "#237cb5" }}>
            <Smartphone className="h-3.5 w-3.5" strokeWidth={2.8} />
            Télétravail &amp; multimédia
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Le protocole pour vos objets connectés
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Avec l&apos;essor du travail à distance dans les Yvelines,
            sécuriser son matériel informatique, ses écrans et ses
            consoles de jeux est devenu un enjeu majeur lors
            d&apos;un déménagement.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Card 1 : Numérotation des branchements */}
          <div className="relative bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-7 overflow-hidden">
            <span className="absolute top-0 left-0 h-1.5 w-full bg-[#237cb5]" />
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#cfdbe6] text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-[#237cb5] mb-5">
              <Cable className="h-3.5 w-3.5" strokeWidth={2.8} />
              Protocole 01 · Branchements
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-xl bg-[#237cb5] text-white">
                <Camera className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[19px] text-[#0f1d2b] leading-tight">
                La numérotation des branchements
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Avant de débrancher votre box internet, votre
              ordinateur ou votre installation TV, prenez une photo
              des câbles à l&apos;arrière avec votre smartphone.
              Attachez chaque câble avec un lien de serrage et
              rangez-les dans le même carton que l&apos;appareil
              concerné pour un remontage immédiat et sans casse-tête
              à l&apos;arrivée.
            </p>
            <ul className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <li className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Photo des branchements avant débranchement
              </li>
              <li className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Liens de serrage pour grouper les câbles
              </li>
              <li className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Câbles dans le même carton que l&apos;appareil
              </li>
            </ul>
          </div>

          {/* Card 2 : Armure pour écrans plats */}
          <div className="relative bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-7 overflow-hidden">
            <span className="absolute top-0 left-0 h-1.5 w-full bg-[#FF5722]" />
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#cfdbe6] text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-[#FF5722] mb-5">
              <Tv className="h-3.5 w-3.5" strokeWidth={2.8} />
              Protocole 02 · Écrans plats
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-xl bg-[#FF5722] text-white">
                <ShieldHalf className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[19px] text-[#0f1d2b] leading-tight">
                L&apos;armure pour les écrans plats
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Les dalles de vos écrans et moniteurs sont extrêmement
              sensibles aux rayures micro-superficielles. Enveloppez-les
              d&apos;abord dans une feuille de papier kraft doux pour
              protéger la surface, puis entourez l&apos;appareil avec
              deux couches de notre film à bulles haute densité avant
              de le glisser verticalement dans un carton renforcé
              double cannelure bien calé.
            </p>
            <ul className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <li className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#FF5722]" strokeWidth={2.6} />
                Papier kraft doux (couche 1)
              </li>
              <li className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#FF5722]" strokeWidth={2.6} />
                Film à bulles haute densité × 2 (couche 2)
              </li>
              <li className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#FF5722]" strokeWidth={2.6} />
                Transport vertical en carton double cannelure
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-mareil-sur-mauldre-cta-bulles"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Papier bulle &amp; kraft
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-mareil-sur-mauldre-cta-renforces"
          >
            <Monitor className="h-4 w-4" strokeWidth={2.6} />
            Cartons renforcés double cannelure
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-mareil-sur-mauldre-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
